import React from "react";
import {
  MdBusiness,
  MdEmail,
  MdPerson,
  MdPhone,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import type { Center, CenterCreate } from "types/domain";
import ActionButton from "components/ui/ActionButton";
import { useLanguage } from "context/LanguageContext";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CenterCreate) => Promise<Center>;
  onCreated: (centerId: string) => void;
}

const INITIAL: CenterCreate = {
  name: "",
  code: "",
  address: "",
  phone: "",
  manager: {
    full_name: "",
    email: "",
    phone: "",
    password: "",
  },
  send_email: true,
};

export default function CreateCenterModal({
  isOpen,
  onClose,
  onSubmit,
  onCreated,
}: Props) {
  const { t } = useLanguage();
  const [form, setForm] = React.useState<CenterCreate>(INITIAL);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);

  if (!isOpen) return null;

  const setField = (
    field: keyof Omit<CenterCreate, "manager">,
    value: string
  ) => setForm((prev) => ({ ...prev, [field]: value }));

  const setManagerField = (
    field: keyof CenterCreate["manager"],
    value: string
  ) =>
    setForm((prev) => ({
      ...prev,
      manager: { ...prev.manager, [field]: value },
    }));

  const ERROR_MAP: Record<string, string> = {
    "A center with this name already exists": "centers.create.errorNameExists",
    "A user with this email already exists": "centers.create.errorEmailExists",
    "A user with this code already exists": "centers.create.errorCodeExists",
  };

  const mapError = (msg: string): string =>
    ERROR_MAP[msg] ? t(ERROR_MAP[msg]) : t(msg);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const createdCenter = await onSubmit({
        name: form.name,
        code: form.code || undefined,
        address: form.address || undefined,
        phone: form.phone || undefined,
        manager: {
          full_name: form.manager.full_name,
          email: form.manager.email,
          phone: form.manager.phone || undefined,
          password: form.manager.password || undefined,
        },
        send_email: form.send_email,
      });
      setForm(INITIAL);
      onClose();
      onCreated(createdCenter.id);
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : "centers.create.error";
      setError(mapError(raw));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-line bg-surface px-4 py-2.5 text-sm text-ink outline-none transition-colors focus:border-brand-500 focus:ring-1 focus:ring-brand-500";

  return (
    <div
      className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-surface p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-ink">
              {t("centers.create.title")}
            </h3>
            <p className="mt-1 text-xs text-ink-muted">
              {t("centers.create.subtitle")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Center info */}
          <div className="rounded-xl border border-line bg-white/70 p-4 dark:bg-surface-muted/30">
            <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
              <MdBusiness className="h-4 w-4" />
              {t("centers.create.centerInfo")}
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-ink">
                  {t("centers.field.name")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  className={inputClass}
                  placeholder={t("centers.field.namePlaceholder")}
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-ink">
                  {t("centers.field.code")}
                </label>
                <input
                  className={inputClass}
                  placeholder={t("centers.field.codePlaceholder")}
                  value={form.code}
                  onChange={(e) => setField("code", e.target.value)}
                  maxLength={20}
                />
                <p className="mt-1 text-xs text-ink-subtle">
                  {t("centers.field.codeHint")}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-ink">
                  {t("centers.field.address")}
                </label>
                <input
                  className={inputClass}
                  placeholder={t("centers.field.addressPlaceholder")}
                  value={form.address}
                  onChange={(e) => setField("address", e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 flex items-center gap-1.5 text-sm font-medium text-ink">
                  <MdPhone className="h-4 w-4 text-ink-subtle" />
                  {t("centers.field.phone")}
                </label>
                <input
                  type="tel"
                  className={inputClass}
                  placeholder={t("centers.field.phonePlaceholder")}
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <hr className="border-line" />

          {/* Manager info */}
          <div className="rounded-xl border border-line bg-white/70 p-4 dark:bg-surface-muted/30">
            <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
              <MdPerson className="h-4 w-4" />
              {t("centers.create.managerInfo")}
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-ink">
                  {t("centers.manager.fullName")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  className={inputClass}
                  placeholder={t("centers.manager.fullNamePlaceholder")}
                  value={form.manager.full_name}
                  onChange={(e) => setManagerField("full_name", e.target.value)}
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 flex items-center gap-1.5 text-sm font-medium text-ink">
                    <MdEmail className="h-4 w-4 text-ink-subtle" />
                    {t("settings.email")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    className={inputClass}
                    placeholder="manager@example.com"
                    value={form.manager.email}
                    onChange={(e) => setManagerField("email", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-ink">
                    {t("settings.phone")}
                  </label>
                  <input
                    type="tel"
                    className={inputClass}
                    placeholder="0901234567"
                    value={form.manager.phone}
                    onChange={(e) => setManagerField("phone", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-ink">
                  {t("centers.manager.password")}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className={inputClass + " pr-10"}
                    placeholder={t("centers.manager.passwordPlaceholder")}
                    value={form.manager.password ?? ""}
                    onChange={(e) =>
                      setManagerField("password", e.target.value)
                    }
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-gray-600"
                  >
                    {showPassword ? (
                      <MdVisibilityOff className="h-5 w-5" />
                    ) : (
                      <MdVisibility className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.send_email}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, send_email: e.target.checked }))
              }
              className="h-4 w-4 rounded border-line text-brand-500 focus:ring-brand-500"
            />
            {t("centers.create.sendEmail")}
          </label>

          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <ActionButton type="button" variant="secondary" onClick={onClose}>
              {t("action.cancel")}
            </ActionButton>
            <ActionButton type="submit" variant="primary" disabled={submitting}>
              {submitting
                ? t("centers.create.submitting")
                : t("centers.create.submit")}
            </ActionButton>
          </div>
        </form>
      </div>
    </div>
  );
}
